import React from 'react';
import { useQuery } from 'urql';
import { MetaAdCampaignObjective } from '@frontend/gql/social/graphql';
import { getMetaAdAccountsForFilters } from '../graphql';

interface AdAccount {
  id: string;
  name: string;
}

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
  hasPaidPartnershipAds?: boolean;
}

interface AdSet {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
}

interface AdObjective {
  id: string;
  name: string;
  objective: MetaAdCampaignObjective;
}

interface AdsFilterFormData {
  adAccounts: AdAccount[];
  adCampaigns: AdCampaign[];
  adSets: AdSet[];
  adObjectives: AdObjective[];
  isFetchingAdAccounts: boolean;
  isFetchingAdsData: boolean;
}

// Helper function to convert MetaAdCampaignObjective enum to human-readable names
const getObjectiveDisplayName = (objective: MetaAdCampaignObjective): string => {
  const objectiveNames: Record<MetaAdCampaignObjective, string> = {
    APP_INSTALLS: 'App Installs',
    BRAND_AWARENESS: 'Brand Awareness',
    CONVERSIONS: 'Conversions',
    EVENT_RESPONSES: 'Event Responses',
    LEAD_GENERATION: 'Lead Generation',
    LINK_CLICKS: 'Link Clicks',
    LOCAL_AWARENESS: 'Local Awareness',
    MESSAGES: 'Messages',
    MOBILE_APP_ENGAGEMENT: 'Mobile App Engagement',
    MOBILE_APP_INSTALLS: 'Mobile App Installs',
    NONE: 'None',
    OFFER_CLAIMS: 'Offer Claims',
    OUTCOME_APP_PROMOTION: 'App Promotion',
    OUTCOME_AWARENESS: 'Awareness',
    OUTCOME_ENGAGEMENT: 'Engagement',
    OUTCOME_LEADS: 'Leads',
    OUTCOME_SALES: 'Sales',
    OUTCOME_TRAFFIC: 'Traffic',
    PAGE_LIKES: 'Page Likes',
    POST_ENGAGEMENT: 'Post Engagement',
    PRODUCT_CATALOG_SALES: 'Product Catalog Sales',
    REACH: 'Reach',
    STORE_VISITS: 'Store Visits',
    VIDEO_VIEWS: 'Video Views',
  };

  return objectiveNames[objective] || objective;
};

// Type definitions for GraphQL response data
interface MetaAdCreative {
  instagramBrandedContent?: unknown;
}

interface MetaAd {
  metaAdMetaAdCreatives?: MetaAdCreative[];
}

interface MetaAdSetData {
  id?: string;
  name?: string;
  metaAds?: MetaAd[];
}

interface MetaCampaignData {
  id?: string;
  name?: string;
  metaAdSets?: MetaAdSetData[];
}

// Helper function to check if an ad has paid partnership content
const adHasPaidPartnership = (ad: MetaAd): boolean => {
  if (!ad?.metaAdMetaAdCreatives) {
    return false;
  }

  return ad.metaAdMetaAdCreatives.some((creative) => {
    // Check if instagramBrandedContent exists and is not null
    return creative?.instagramBrandedContent != null;
  });
};

// Helper function to check if an ad set has paid partnership ads
const adSetHasPaidPartnershipAds = (adSet: MetaAdSetData): boolean => {
  if (!adSet?.metaAds) {
    return false;
  }

  return adSet.metaAds.some(adHasPaidPartnership);
};

// Helper function to safely map an ad set to the AdSet interface with paid partnership check
const mapAdSetWithPartnershipCheck = (adSet: MetaAdSetData): AdSet | null => {
  // Use explicit null checks and provide safe fallbacks
  const adSetId = adSet?.id;
  const adSetName = adSet?.name;

  if (!adSetId || !adSetName) {
    return null;
  }

  return {
    id: adSetId,
    name: adSetName,
    hasPaidPartnershipAds: adSetHasPaidPartnershipAds(adSet),
  };
};

// Helper function to process ad sets for a campaign and determine if campaign has paid partnership ads
const processCampaignAdSets = (campaign: MetaCampaignData): { adSets: AdSet[]; hasPaidPartnershipAds: boolean } => {
  if (!campaign?.metaAdSets) {
    return { adSets: [], hasPaidPartnershipAds: false };
  }

  const adSets: AdSet[] = [];
  let campaignHasPaidPartnershipAds = false;

  campaign.metaAdSets.forEach((adSet) => {
    const mappedAdSet = mapAdSetWithPartnershipCheck(adSet);
    if (mappedAdSet) {
      adSets.push(mappedAdSet);
      if (mappedAdSet.hasPaidPartnershipAds) {
        campaignHasPaidPartnershipAds = true;
      }
    }
  });

  return { adSets, hasPaidPartnershipAds: campaignHasPaidPartnershipAds };
};

export const useAdsFilterFormData = (clientId?: string): AdsFilterFormData => {
  const [{ data: adAccountsData, fetching: isFetchingAdAccounts, error: adAccountsError }] = useQuery({
    query: getMetaAdAccountsForFilters,
    variables: {
      clientId: clientId || '',
    },
    pause: !clientId,
  });

  const metaAdAccounts = React.useMemo(() => {
    return adAccountsData?.client?.metaAdAccounts?.filter((account): account is NonNullable<typeof account> => {
      // GraphQL already filters by ACTIVE/ANY_ACTIVE status, just ensure account exists
      return !!account;
    }) || [];
  }, [adAccountsData?.client?.metaAdAccounts]);

  // Log errors for debugging
  React.useEffect(() => {
    if (adAccountsError) {
      console.error('❌ Ad Accounts Error:', adAccountsError);
    }
  }, [adAccountsError]);

  const mappedAdAccounts = React.useMemo(() => {
    return metaAdAccounts.map((account) => ({
      id: account.id || '',
      name: account.name || '',
    })).filter((account) => account.id && account.name);
  }, [metaAdAccounts]);

  const mappedAdCampaigns = React.useMemo(() => {
    const campaigns: AdCampaign[] = [];
    metaAdAccounts.forEach((account) => {
      if (!account.metaAdCampaigns) {
        return;
      }

      account.metaAdCampaigns.forEach((campaign) => {
        if (!campaign?.id || !campaign?.name) {
          return;
        }

        const { adSets, hasPaidPartnershipAds } = processCampaignAdSets(campaign);

        campaigns.push({
          id: campaign.id,
          name: campaign.name,
          adSets,
          hasPaidPartnershipAds,
        });
      });
    });

    return campaigns;
  }, [metaAdAccounts]);

  const mappedAdSets = React.useMemo(() => {
    const adSets: AdSet[] = [];
    mappedAdCampaigns.forEach((campaign) => {
      if (campaign.adSets) {
        adSets.push(...campaign.adSets);
      }
    });
    return adSets;
  }, [mappedAdCampaigns]);

  // Extract unique objectives from campaigns
  const mappedAdObjectives = React.useMemo(() => {
    const objectiveSet = new Set<MetaAdCampaignObjective>();

    metaAdAccounts.forEach((account) => {
      if (account.metaAdCampaigns) {
        account.metaAdCampaigns.forEach((campaign) => {
          if (campaign?.objective) {
            objectiveSet.add(campaign.objective);
          }
        });
      }
    });

    return Array.from(objectiveSet).map((objective) => ({
      id: objective,
      name: getObjectiveDisplayName(objective),
      objective,
    }));
  }, [metaAdAccounts]);

  return {
    adAccounts: mappedAdAccounts,
    adCampaigns: mappedAdCampaigns,
    adSets: mappedAdSets,
    adObjectives: mappedAdObjectives,
    isFetchingAdAccounts,
    isFetchingAdsData: isFetchingAdAccounts, // Generic loading state for all ads data
  };
};
